// system.* — server-only transient notifications.
//
// All system events are server → client only and are NOT carried over the
// per-room seq (no `seq` / `ts` on the event itself). They are excluded
// from the session buffer / snapshots so that a maintenance countdown
// from the past never replays on resume.
// (protocol-events.md §7)
import { z } from 'zod';
/**
 * `system.maintenance.scheduled` — broadcast announcement of a planned
 * maintenance window. Also re-sent on `room.welcome` if a window is
 * currently active (要検証: protocol-events.md §7.6).
 */
export const systemMaintenanceScheduledSchema = z.object({
    t: z.literal('system.maintenance.scheduled'),
    /** Epoch ms at which maintenance begins. */
    startAt: z.number().int().nonnegative(),
    /** Estimated maintenance duration in ms. */
    durationMs: z.number().int().nonnegative().optional(),
    /** Free-form reason shown to users. */
    reason: z.string().optional(),
});
/**
 * `system.maintenance.countdown` — broadcast every minute (or whatever the
 * scheduler is configured for) until shutdown.
 */
export const systemMaintenanceCountdownSchema = z.object({
    t: z.literal('system.maintenance.countdown'),
    remainingSec: z.number().int().nonnegative(),
});
/**
 * `system.maintenance.cancelled` — broadcast when an upcoming maintenance
 * window is cancelled (admin pulls it before the start moment).
 *
 * Phase 7 Task 7-6 (consult 2026-05-05 確定):
 * - MVP は **同時 active 1 件のみ** を保持する設計のため、ペイロードは `t`
 *   のみで「全予約をリセット」を意味する. 将来複数予約に拡張するなら
 *   `scheduleId` 等を追加する.
 * - dispatcher の admin endpoint (`DELETE /admin/maintenance/scheduled`) が
 *   発火源で、dispatcher → 全 WS server (internal HTTP) → 全 client の経路で
 *   配信される.
 * - countdown setInterval は cancel と同時に止まるので、cancelled の後に
 *   countdown が来ることはない.
 */
export const systemMaintenanceCancelledSchema = z.object({
    t: z.literal('system.maintenance.cancelled'),
});
/**
 * `system.announce.posted` — broadcast a service-provider announcement to all
 * connected clients (Phase 9.6, consult 2026-05-15 確定).
 *
 * Differences from `system.maintenance.*`:
 * - **複数 active 並列** (= maintenance は単一 active 排他、announce は最大 10 件)
 * - **TTL hold + DB 永続** (announce persistence 2026-07: 当初は in-memory のみで
 *   dispatcher 再起動で全消失する許容だったが、TTL 上限 30 日化とセットで
 *   dispatcher DB に永続化. 再起動時は dispatcher が hydrate + resync push、
 *   WS server は boot 時に pull で復元する. wire 形式は不変)
 * - **新規入室時の unicast 再送** は同パターン (= JoinService が welcome 直後
 *   に active 全件を unicast 再送する).
 *
 * 用途: 「気軽なお知らせ」「短期警告」「キャンペーン情報」「新機能告知
 * (長期掲示)」. メンテ予告は maintenance scheduled の仕組みを使う運用.
 *
 * Transient: maintenance と同じく `seq` / `ts` を持たず、ring buffer や
 * snapshot には乗らない. resume では再送されない (= 古い announce が時間差で
 * 復活しないように).
 */
export const ANNOUNCE_SEVERITY = ['info', 'warning', 'critical'];
export const systemAnnouncePostedSchema = z.object({
    t: z.literal('system.announce.posted'),
    /**
     * ULID identifying this announce. dispatcher が POST /admin/announces で
     * 生成. cleared broadcast / GET active 一覧 / admin による個別削除で同 id
     * を参照する.
     */
    id: z.string().min(1),
    /** UI 表示する本文 (1..512 chars). */
    message: z.string().min(1).max(512),
    /** Epoch ms when posted (server stamp、admin endpoint 受信時刻). */
    postedAt: z.number().int().nonnegative(),
    /**
     * Epoch ms when announce auto-expires. 絶対時刻を持つことで client / server
     * 間の clock skew に強く、reconnect 後 (= 経過時間が見えない) も正しく扱える.
     * server 側 sweeper は `now >= expiresAt` で evict する.
     */
    expiresAt: z.number().int().nonnegative(),
    /**
     * UI severity hint, optional. client は色分け (info=青, warning=黄, critical=赤)
     * 等で視覚化. 省略時は `'info'` 相当として扱う.
     */
    severity: z.enum(ANNOUNCE_SEVERITY).optional(),
});
/**
 * `system.announce.cleared` — broadcast when an announce is removed
 * (admin による明示削除 / TTL expiry / max 10 件 cap 超過時の古い順 evict).
 *
 * 単一 id 対象なので、複数同時 active のうち 1 件だけ消えるケースを表現できる
 * (= maintenance.cancelled が「全予約リセット」を表すのと違って targeted).
 */
export const systemAnnounceClearedSchema = z.object({
    t: z.literal('system.announce.cleared'),
    /** ULID of the announce being cleared. */
    id: z.string().min(1),
});
/**
 * `system.error` — unicast back to the offending client.
 *
 * `detail` carries debugging context and MUST be omitted in production;
 * the server-side codec configuration is responsible for stripping it.
 * (protocol-events.md §7.5)
 */
export const systemErrorSchema = z.object({
    t: z.literal('system.error'),
    code: z.enum([
        'invalid-message',
        'rate-limited',
        'internal-error',
        'unsupported-protocol-version',
        /**
         * Phase 7 Task 7-5: 権限不足の汎用シグナル. 現状 `whiteboard.clear` を
         * `clearOpen=false` のルームで `Perm.CLEAR` 無しに送ったケースで使う.
         * (admin.kick は `admin.kick-rejected { reason: 'no-permission' }` と
         * 個別の reject イベントを使うので本コードは投げない、`whiteboard.imagepaste-*`
         * も同様に専用の `imagepaste-rejected` を使う). 将来 imagepaste 等で
         * 専用 reject イベントを持たない command が現れたら同コードを再利用する.
         */
        'no-permission',
    ]),
    message: z.string().optional(),
    detail: z.unknown().optional(),
});
//# sourceMappingURL=system.js.map